// @flow

import React, { useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { CdnImage } from '@ta/common.cdn';
import classNames from '@ta/common.classnames';
import Localize, { UnsafeLocalize, useIntlFormatter, IntlFormatter } from '@ta/platform.i18n';
import { Link } from '@ta/common.routing';
import Icon from '@ta/core-ui.icon';
import { Device } from '@ta/lithium.context';
import { useGARecorder } from '@ta/platform.interactions';
import styles from './10PercentOffPromoScreen.css';

type ExternalProps = {|
  onContinue: () => void,
|};

type Props = {|
  ...ExternalProps,
  geoId: ?number,
|};

/**
 * Generates a pair of date/time that is two weeks from now.
 *
 * note: we should NOT be doing this, see https://jira.tamg.io/browse/MEMX-1391
 *
 * @returns {[String, String]}
 */
const generateExpirationInfo = (formatter: IntlFormatter) => {
  const date = new Date(); // today
  date.setDate(date.getDate() + 14); // two weeks from now
  return [
    formatter.formatDate('date_short', date), // 12/17/2019 (for US)
    formatter.formatDate('time_12_24', new Date(0, 0, 0, 23, 59)), // 11:59 PM (local time)
  ];
};

// Show a success message to the user when the SUAS flow is complete.
// note: if there are any problems with sending the email, we are kind of screwed here, see https://jira.tamg.io/browse/MEMX-1388.
const PostSignUpAttractionsPromo = (props: Props) => {
  const formatter = useIntlFormatter();
  const [expirationDate, expirationTime] = generateExpirationInfo(formatter);
  const attractionsLink = props.geoId ? `/Attractions-g${props.geoId}` : '/Attractions';
  const { viewportCategory } = React.useContext(Device);
  const imagePath =
    viewportCategory === 'MOBILE'
      ? 'img2/memx/attr-promo-mobile.jpg'
      : 'img2/memx/attr-promo-desktop.jpg';

  const record = useGARecorder();
  const track = useCallback(
    (action: string) => {
      record({
        module: 'signup_promo_prompt',
        action,
      });
    },
    [record],
  );

  useEffect(() => {
    track('shown_promo');
  }, [track]);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.content}>
        <span className={styles.icon}>
          <Icon name="attractions" /> {/* Attractions Icon */}
        </span>
        <div className={styles.offer}>
          <div className={styles.header}>
            {' '}
            <Localize id="jfy" />{' '}
          </div>{' '}
          {/* Just for you */}
          <div className={styles.promoLine}>
            {/* <span class="{0}">10</span><span class=“{1}">%</span><br/> off any tour or activity */}
            <UnsafeLocalize
              id="10_off_tour_activity"
              args={{ '0': styles.promoAmount, '1': styles.promoPercent }}
            />
          </div>{' '}
        </div>
        {/* Check your email for your discount code and get ready to do more for less. */}
        <div className={styles.checkEmail}>
          {' '}
          <Localize id="check_email_subheader" />{' '}
        </div>{' '}
        <div className={styles.ctaSection}>
          <div className={styles.buttonContainer}>
            <Link href={attractionsLink}>
              <button
                className={styles.customButtonType}
                onClick={() => {
                  track('accept_promo');
                }}
              >
                <Localize id="discover_ttd" /> {/* Discover things to do */}
              </button>
            </Link>
          </div>
          <div
            className={classNames('ui_link', styles.continueLink)}
            onClick={() => {
              props.onContinue();
              track('close_promo');
            }}
          >
            {' '}
            <Localize id="continue_sub_cta" /> {/* Continue where you left off */}
          </div>
        </div>
        <div className={styles.footerText}>
          <div>
            <UnsafeLocalize id="free_cancel_ahead" args={{ '0': styles.footerStart }} />
          </div>{' '}
          {/* <span class="{0}">No commitment:</span> Cancel for free up to 24 hours ahead.   */}
          <div>
            <UnsafeLocalize
              id="expiry_date_cta"
              args={{ sDate: expirationDate, sTime: expirationTime, '0': styles.footerStart }}
            />
            {/* <span class="{0}">But don’t wait:</span> Expires on {sDate} at {sTime} */}
          </div>{' '}
        </div>
      </div>

      <div className={styles.imageContainer}>
        <CdnImage src={imagePath} className={styles.image} />
      </div>
    </div>
  );
};

export default connect<Props, ExternalProps, _, _, _, _>(state => ({
  geoId: state.route.geo,
}))(PostSignUpAttractionsPromo);
